import type { Post } from '@/@types/post'
import usePostDrawer from '@/utils/hooks/usePostDrawer'
import { useIsMobile } from '@/utils/hooks/useMediaQuery'
import { ActionIcon, Box, DrawerProps, ScrollArea, UnstyledButton } from '@mantine/core'
import { IconArrowLeft, IconX } from '@tabler/icons-react'
import clsx from 'clsx'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useRef } from 'react'
import NavBar, { NavBarProps } from '../NavBar'
import Page from '../Page'
import PostDrawerPageContainer from './PostDrawerPageContainer'
import styles from './PostDrawer.module.css'
import PostDrawerContent from './PostDrawerContent'

export interface PostDrawerProps extends DrawerProps {
	data: Post | undefined
}
export default function PostDrawer({ data: post, opened, className, ...props }: PostDrawerProps) {
	const { close } = usePostDrawer()
	const isMobile = useIsMobile()
	const scrollAreaRef = useRef<HTMLDivElement | null>(null)
	const isDrawerOpened = opened && !!post

	const renderCloseButton: NonNullable<NavBarProps['renderLeftSection']> = () => {
		return (
			<UnstyledButton
				className={styles.closeButton}
				aria-label="关闭动态详情"
				title="关闭动态详情"
				onClick={close}
			>
				<IconArrowLeft size={20} />
			</UnstyledButton>
		)
	}

	useEffect(() => {
		scrollAreaRef.current?.scrollTo({ top: 0 })
	}, [post?.id])

	return (
		<Box className={clsx(styles.container, className)}>
			<AnimatePresence>
				{isDrawerOpened && (
					<motion.div
						key="post-drawer-mask"
						className={styles.mask}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
						onClick={close}
					/>
				)}
				{isDrawerOpened && (
					<PostDrawerPageContainer key="post-drawer-content">
						<Page
							header={
								<NavBar
									className={styles.mobileHeader}
									title="动态详情"
									showBackButton={false}
									renderLeftSection={isMobile ? () => null : renderCloseButton}
								/>
							}
							className={styles.page}
						>
							<ScrollArea
								h="100%"
								w="100%"
								scrollbars="y"
								overscrollBehavior="contain"
								viewportRef={scrollAreaRef}
							>
								<PostDrawerContent post={post} scrollAreaRef={scrollAreaRef} />
							</ScrollArea>
						</Page>
						{isMobile && (
							<ActionIcon
								aria-label="关闭动态详情"
								title="关闭动态详情"
								variant="default"
								size={56}
								radius="50%"
								className={styles.mobileCloseButton}
								onClick={close}
							>
								<IconX />
							</ActionIcon>
						)}
					</PostDrawerPageContainer>
				)}
			</AnimatePresence>
		</Box>
	)
}
